import { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/hooks/useAuth';
import { SharedSection } from '@/components/SharedSection';
import { MobileTemplateViewer } from '@/components/MobileTemplateViewer';
import { SearchModal } from '@/components/SearchModal';
import { SampleChecklistModal } from '@/components/SampleChecklistModal';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { useToast } from '@/hooks/use-toast';
import Header from '@/components/Header';
import Footer from '@/components/Footer';
import { CanonicalTag } from '@/components/CanonicalTag';
import { shareTokenLimiter } from '@/lib/rateLimiter';
import { 
  User, 
  Calendar,
  ArrowRight,
  Loader2,
  AlertCircle
} from 'lucide-react';

interface SharedSectionData {
  section_name: string;
  section_type: string;
  target_date: string | null;
  countdown_enabled: boolean;
  phases: Array<{
    id: string;
    phase_title: string;
    display_order: number;
  }>;
  items: Array<{
    id: string;
    item_text: string;
    phase_id: string | null;
    display_order: number;
    is_ready: boolean;
    is_bundled: boolean;
    is_secured: boolean;
    notes: string | null;
  }>;
}

interface ShareInfo {
  sender_id: string;
  is_accepted: boolean;
  expires_at: string;
}

export const SharedChecklist = () => {
  const { token } = useParams<{ token: string }>();
  const navigate = useNavigate();
  const { user, loading: authLoading } = useAuth();
  const { toast } = useToast();
  
  const [sectionData, setSectionData] = useState<SharedSectionData | null>(null);
  const [shareInfo, setShareInfo] = useState<ShareInfo | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [accepting, setAccepting] = useState(false);
  const [selectedPhaseId, setSelectedPhaseId] = useState<string | null>(null);
  const [currentPhase, setCurrentPhase] = useState<string>('');
  const [isSearchOpen, setIsSearchOpen] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [preloadedModalData, setPreloadedModalData] = useState<any[]>([]);

  useEffect(() => {
    if (!token) {
      setError('Invalid share link');
      setLoading(false);
      return;
    }

    loadSharedData();
  }, [token]);

  const loadSharedData = async () => {
    try {
      setLoading(true);
      setError(null);

      // Client-side rate limiting check (10 attempts per minute)
      const rateLimitKey = `share-token-${token}`;
      if (!shareTokenLimiter.checkLimit(rateLimitKey)) {
        const resetTimeMs = shareTokenLimiter.getResetTime(rateLimitKey);
        const resetTimeSec = Math.ceil(resetTimeMs / 1000);
        setError(`Too many attempts. Please wait ${resetTimeSec} seconds and try again.`);
        return;
      }

      // Use secure validation with database rate limiting
      const { data: shareData, error: shareError } = await supabase
        .rpc('get_shared_checklist', { _token: token })
        .single();

      if (shareError || !shareData) {
        // Check if it's a rate limit error from database
        if (shareError?.message?.includes('Rate limit exceeded')) {
          setError('Too many attempts. Please try again in an hour.');
        } else {
          setError('Share not found or has expired');
        }
        return;
      }

      // Check if expired
      if (new Date(shareData.expires_at) < new Date()) {
        setError('This checklist has already been added or has expired');
        return;
      }

      setShareInfo({
        sender_id: shareData.sender_id,
        is_accepted: shareData.is_accepted,
        expires_at: shareData.expires_at,
      });

      setSectionData({
        section_name: shareData.section_name,
        section_type: shareData.section_type,
        target_date: shareData.target_date,
        countdown_enabled: shareData.countdown_enabled,
        phases: Array.isArray(shareData.phases) ? shareData.phases as any[] : [],
        items: Array.isArray(shareData.items) ? shareData.items as any[] : [],
      });

      // Log view action securely (no email exposure)
      const tokenHash = shareData.share_id; // This is actually the share_id from the RPC result
      await supabase.rpc('log_share_action_secure', {
        _share_id: shareData.share_id,
        _action_type: 'viewed',
        _attempt_result: 'success',
        _user_agent: navigator.userAgent,
      });

    } catch (error: any) {
      // Handle rate limiting errors specifically
      if (error?.message?.includes('Rate limit exceeded')) {
        setError('Too many attempts. Please try again in an hour.');
      } else {
        setError('Failed to load shared checklist');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleDeclineShare = async () => {
    if (!user || !token) return;

    try {
      // Get share record to log decline securely
      const { data: shareRecord } = await supabase
        .from('checklist_shares')
        .select('id, token_hash')
        .eq('share_token', token)
        .single();

      if (shareRecord) {
        await supabase.rpc('log_share_action_secure', {
          _share_id: shareRecord.id,
          _action_type: 'declined',
          _token_hash: shareRecord.token_hash,
          _attempt_result: 'success',
          _user_agent: navigator.userAgent,
        });
      }

      toast({
        title: "No problem!",
        description: "You can always come back to this link later if you change your mind."
      });

      navigate('/');
    } catch (error) {
      // Still navigate away even if logging fails
      navigate('/');
    }
  };

  const handleAcceptShare = async () => {
    if (!user || !shareInfo || !sectionData) return;

    try {
      setAccepting(true);

      // Create the section for the user
      const { data: newSection, error: sectionError } = await supabase
        .from('checklist_sections')
        .insert({
          user_id: user.id,
          section_name: `${sectionData.section_name} (Shared)`,
          section_type: sectionData.section_type,
          target_date: sectionData.target_date,
          countdown_enabled: sectionData.countdown_enabled,
          is_default: false,
          source_section: 'shared',
        })
        .select()
        .single();

      if (sectionError || !newSection) {
        throw new Error('Failed to create section');
      }

      // Create phases if they exist
      if (sectionData.phases.length > 0) {
        const { data: newPhases, error: phasesError } = await supabase
          .from('checklist_phases')
          .insert(
            sectionData.phases.map(phase => ({
              section_id: newSection.id,
              user_id: user.id,
              phase_title: phase.phase_title,
              display_order: phase.display_order,
            }))
          )
          .select();

        if (phasesError) {
          throw new Error('Failed to create phases');
        }

        // Create phase mapping for items
        const phaseMapping = new Map();
        sectionData.phases.forEach((originalPhase, index) => {
          if (newPhases && newPhases[index]) {
            phaseMapping.set(originalPhase.id, newPhases[index].id);
          }
        });

        // Create items
        if (sectionData.items.length > 0) {
          const { error: itemsError } = await supabase
            .from('checklist_items')
            .insert(
              sectionData.items.map(item => ({
                section_id: newSection.id,
                user_id: user.id,
                phase_id: item.phase_id ? phaseMapping.get(item.phase_id) || null : null,
                item_text: item.item_text,
                display_order: item.display_order,
                is_ready: false, // Reset status for new user
                is_bundled: false,
                is_secured: false,
                notes: item.notes,
              }))
            );

          if (itemsError) {
            throw new Error('Failed to create items');
          }
        }
      } else {
        // Create items without phases
        if (sectionData.items.length > 0) {
          const { error: itemsError } = await supabase
            .from('checklist_items')
            .insert(
              sectionData.items.map(item => ({
                section_id: newSection.id,
                user_id: user.id,
                phase_id: null,
                item_text: item.item_text,
                display_order: item.display_order,
                is_ready: false,
                is_bundled: false,
                is_secured: false,
                notes: item.notes,
              }))
            );

          if (itemsError) {
            throw new Error('Failed to create items');
          }
        }
      }

      // Log acceptance securely
      const { data: shareRecord } = await supabase
        .from('checklist_shares')
        .select('id, token_hash')
        .eq('share_token', token)
        .single();
        
      if (shareRecord) {
        await supabase.rpc('log_share_action_secure', {
          _share_id: shareRecord.id,
          _action_type: 'accepted',
          _token_hash: shareRecord.token_hash,
          _attempt_result: 'success',
          _security_notes: { section_created: true },
          _user_agent: navigator.userAgent,
        });
      }

      toast({
        title: 'Checklist added successfully!',
        description: `"${sectionData.section_name}" has been added to your checklists.`,
      });

      // Redirect to main checklists page
      navigate('/');

    } catch (error) {
      toast({
        title: 'Failed to accept checklist',
        description: 'Please try again later.',
        variant: 'destructive',
      });
    } finally {
      setAccepting(false);
    }
  };

  if (authLoading || loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-background to-secondary/20 flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="h-8 w-8 animate-spin mx-auto mb-4" />
          <p className="text-muted-foreground font-body">Loading shared checklist...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-background to-secondary/20 flex items-center justify-center">
        <Card className="max-w-md mx-auto">
          <CardContent className="p-6 text-center">
            <AlertCircle className="h-12 w-12 text-destructive mx-auto mb-4" />
            <h2 className="text-xl font-display font-semibold mb-2">Unable to Load Checklist</h2>
            <p className="text-muted-foreground font-body leading-[1.7] mb-4">{error}</p>
            <Button onClick={() => navigate('/')} variant="outline">
              Go to Home
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (!sectionData) {
    return null;
  }

  // Transform shared data to match component expectations
  const transformedPhases = sectionData.phases.map(phase => ({
    id: phase.id,
    title: phase.phase_title,
  }));

  const phaseTitles = sectionData.phases.reduce((acc, phase) => {
    acc[phase.id] = phase.phase_title;
    return acc;
  }, {} as Record<string, string>);

  // Group items by phase for preview display
  const phaseItemsMap = sectionData.items.reduce((acc, item) => {
    if (item.phase_id) {
      if (!acc[item.phase_id]) {
        acc[item.phase_id] = [];
      }
      acc[item.phase_id].push({
        id: item.id,
        item: item.item_text,
        comment: item.notes || '',
      });
    }
    return acc;
  }, {} as Record<string, Array<{ id: string; item: string; comment: string }>>);

  const handlePhaseSelect = (phaseId: string, phaseTitle: string) => {
    setSelectedPhaseId(phaseId);
    setCurrentPhase(phaseTitle);
    
    // Transform shared data to match SampleChecklistModal format
    const phaseItems = sectionData.items
      .filter(item => item.phase_id === phaseId)
      .sort((a, b) => a.display_order - b.display_order)
      .map(item => ({
        id: item.id,
        target: item.is_secured,
        ready: item.is_ready,
        insert: item.is_bundled,
        item: item.item_text,
        comment: item.notes || '',
        url: undefined // Shared checklists don't have URLs
      }));
    
    setPreloadedModalData(phaseItems);
    setIsModalOpen(true);
  };

  // Remove unused variables
  const unPhasedItems = sectionData.phases.length === 0 
    ? sectionData.items.sort((a, b) => a.display_order - b.display_order)
    : [];

  return (
    <div className="min-h-screen">
      <Helmet>
        <title>Shared Checklist | Sink Bike Run</title>
        <meta name="description" content="View and save this shared triathlon race day checklist to your account. Comprehensive race preparation guidance from fellow athletes." />
        <meta name="robots" content="noindex" />
      </Helmet>
      <CanonicalTag url={`/shared/${token}`} />
      <Header
        showSearch={false}
        showAuth={false}
        showAdminControls={false}
        showNavigation={false}
      />
      
      <main 
        className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8"
        role="main"
        aria-label="Shared triathlon checklist"
      >
        <div className="max-w-4xl mx-auto">
          {/* Hero Section - Shared Checklist */}
          <div className="mb-16">
            <div className="bg-card/50 backdrop-blur-sm rounded-2xl shadow-card p-8 border border-primary/10 text-center bg-gradient-to-br from-card/40 via-card/50 to-primary/5">
              <h1 className="text-3xl font-display font-bold text-foreground mb-4">
                Shared Event Checklist
              </h1>
              <p className="text-muted-foreground font-body leading-[1.7] mb-8 text-lg">
                You've been sent an event checklist—ready for you to make it your own. Save it to your account, start checking things off, and stay on top of your race-day prep with a system built for triathletes.
              </p>
              
               {/* Authentication-based actions */}
               {!user && !authLoading ? (
                 <div className="flex justify-center">
                   <Button 
                     onClick={() => navigate(`/auth?returnTo=${encodeURIComponent(window.location.pathname)}&shareToken=${token}`)}
                     size="lg"
                     className="flex items-center gap-2"
                   >
                     <User className="h-4 w-4" />
                     Sign Up or Sign In
                   </Button>
                 </div>
               ) : user ? (
                 <div className="space-y-4">
                     <p className="text-sm text-muted-foreground font-body leading-[1.7]">
                       This link expires in 30 days.
                     </p>
                   <div className="flex flex-col sm:flex-row gap-4 justify-center">
                     <Button 
                       onClick={handleAcceptShare}
                       disabled={accepting}
                       size="lg"
                       className="flex items-center gap-2"
                     >
                       {accepting ? (
                         <Loader2 className="h-4 w-4 animate-spin" />
                       ) : (
                         <ArrowRight className="h-4 w-4" />
                       )}
                       {accepting ? 'Adding...' : 'Add to My Checklists'}
                     </Button>
                     <Button 
                       variant="outline"
                       onClick={handleDeclineShare}
                       disabled={accepting}
                       size="lg"
                     >
                       No Thanks
                     </Button>
                   </div>
                 </div>
               ) : null}
            </div>
          </div>

          {/* Checklist Content */}
          <div id="shared-checklist-content">
            {sectionData.phases.length > 0 ? (
            <SharedSection
              id="shared-section"
              name={sectionData.section_name}
              phases={transformedPhases}
              phaseTitles={phaseTitles}
              onPhaseSelect={handlePhaseSelect}
              targetDate={sectionData.target_date ? new Date(sectionData.target_date) : null}
              countdownEnabled={sectionData.countdown_enabled}
              phaseItems={phaseItemsMap}
              showWatermark={false}
            />
          ) : (
            <Card className="mb-6">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  {sectionData.section_name}
                </CardTitle>
                <div className="flex flex-wrap gap-2 mt-2">
                  {sectionData.target_date && (
                    <Badge variant="outline" className="flex items-center gap-1">
                      <Calendar className="h-3 w-3" />
                      Event Date: {new Date(sectionData.target_date).toLocaleDateString()}
                    </Badge>
                  )}
                </div>
              </CardHeader>
            </Card>
          )}
          </div>
        </div>
      </main>
      
      {/* Search Modal */}
      <SearchModal 
        isOpen={isSearchOpen}
        onClose={() => setIsSearchOpen(false)}
        sections={[]}
        userSections={[]}
        templates={[]}
        phaseTitles={{}}
        onResultSelect={() => {}}
      />
      
      {/* Checklist Modal */}
      <SampleChecklistModal 
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        phaseId={selectedPhaseId || ''}
        phaseTitle={currentPhase}
        sectionName={sectionData?.section_name || ''}
        isSampleChecklist={true}
        isSharedChecklist={true}
        preloadedItems={preloadedModalData}
      />
      
      <Footer />
    </div>
  );
};